import React, { useState, useEffect } from "react";
import "./BarChartComponent.css";

const CHART_HEIGHT = 300; // px — drawable area above the x-axis
const Y_STEPS = 5;
const LABEL_HEIGHT = 52; // px — space below x-axis for labels

const BarChartComponent = ({ data = [], xKey, yKey, title }) => {
  const [animated, setAnimated] = useState(false);

  const maxVal = Math.max(...data.map((d) => d[yKey]), 1);
  const rawStep = maxVal / Y_STEPS;
  const stepVal = Math.ceil(rawStep / 5) * 5 || Math.ceil(rawStep) || 1;
  const axisMax = stepVal * Y_STEPS;

  // ticks: 0 at bottom → axisMax at top
  const ticks = Array.from({ length: Y_STEPS + 1 }, (_, i) => i * stepVal);

  useEffect(() => {
    setAnimated(false);
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setAnimated(true)),
    );
    return () => cancelAnimationFrame(id);
  }, [data]);

  return (
    <div className="barchart-cards">
      {title && <h3 className="barchart-title">{title}</h3>}

      <div className="barchart-scroll-outer">
        <div
          className="barchart-scroll-inner"
          style={{ minWidth: data.length * 64 + 52 }}
        >
          {/* ── Y-AXIS ── */}
          <div
            className="barchart-y-axis"
            style={{ height: CHART_HEIGHT + LABEL_HEIGHT }}
          >
            {/*
              Render ticks top→bottom (axisMax down to 0).
              Each tick sits at equal intervals inside the chart zone.
              The extra LABEL_HEIGHT div at the bottom offsets the label area
              so tick positions match the grid lines exactly.
            */}
            <div className="barchart-y-ticks">
              {[...ticks].reverse().map((tick, i) => (
                <div className="barchart-y-tick" key={i}>
                  {tick}
                </div>
              ))}
            </div>
            <div style={{ height: LABEL_HEIGHT, flexShrink: 0 }} />
          </div>

          {/* ── PLOT AREA ── */}
          <div
            className="barchart-plot"
            style={{ height: CHART_HEIGHT + LABEL_HEIGHT }}
          >
            {/* Grid lines inside chart zone only */}
            <div className="barchart-grid" style={{ height: CHART_HEIGHT }}>
              {[...ticks].reverse().map((_, i) => (
                <div className="barchart-grid-line" key={i} />
              ))}
            </div>

            {/* X-axis / zero line */}
            <div className="barchart-x-axis" />

            {/* Bars */}
            <div className="barchart-bars-row">
              {data.map((item, idx) => {
                /*
                  Use px heights derived from CHART_HEIGHT so each bar's
                  top edge lands exactly on the correct grid line.
                  pxHeight = (value / axisMax) * CHART_HEIGHT
                */
                const pxHeight = animated
                  ? (item[yKey] / axisMax) * CHART_HEIGHT
                  : 0;

                return (
                  <div className="barchart-bar-item" key={idx}>
                    <span
                      className="barchart-bar-value"
                      style={{ opacity: animated ? 1 : 0 }}
                    >
                      {item[yKey]}
                    </span>

                    <div
                      className="barchart-bar-col"
                      style={{
                        height: pxHeight,
                        transitionDelay: `${idx * 40}ms`,
                      }}
                      title={`${item[xKey]}: ${item[yKey]}`}
                    />

                    <span className="barchart-bar-label">{item[xKey]}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarChartComponent;
